import {
  compact,
  filter,
  find,
  first,
  forEach,
  includes,
  isEmpty,
  isEqual,
  join,
  map,
  size,
  slice,
  uniq,
} from 'es-toolkit/compat';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { BooleanParam, DelimitedArrayParam, StringParam, useQueryParam, withDefault } from 'use-query-params';

import { MAX_SIDE_BY_SIDE_VIEWERS } from 'components/Procedure/SlidesViewer/constants';
import { Permission } from 'interfaces/permissionOption';
import { Procedure } from 'interfaces/procedure';
import { Slide } from 'interfaces/slide';
import { useEncodedFilters } from './useEncodedFilters';
import { usePermissions } from './usePermissions';
import { useStudyPlatformSettings } from './useStudyPlatformSettings';

const isPreciseStainMatch = (slide: Slide, stainTypes: string[]) =>
  Boolean(slide) && includes(stainTypes, slide.stainingType) && !slide.positiveControl && !slide.negativeControl;

const findSlideById = (slides: Slide[] | undefined, slideId?: string | null) =>
  slides?.find((slide) => slide.id === slideId);

const getSideBySideSelectedSlideIds = (selectedSlideIds?: Array<string | null> | null): string[] =>
  slice(compact(selectedSlideIds || []), 0, MAX_SIDE_BY_SIDE_VIEWERS);

const getSideBySideViewerStainPreferences = (
  viewersStainPreferences?: Array<string | null> | null,
  slides?: Slide[]
): string[] => {
  const viewerStainPreferenceParts = compact(viewersStainPreferences || []);
  const knownSlideStainTypes = uniq(compact(map(slides || [], 'stainingType')));

  if (isEmpty(viewerStainPreferenceParts) || isEmpty(knownSlideStainTypes)) {
    return slice(viewerStainPreferenceParts, 0, MAX_SIDE_BY_SIDE_VIEWERS);
  }

  const viewerStainPreferences: string[] = [];
  let partIndex = 0;

  while (partIndex < size(viewerStainPreferenceParts) && size(viewerStainPreferences) < MAX_SIDE_BY_SIDE_VIEWERS) {
    let matchedStainType: string | undefined;
    for (let endIndex = size(viewerStainPreferenceParts); endIndex > partIndex; endIndex -= 1) {
      const candidateStainType = join(slice(viewerStainPreferenceParts, partIndex, endIndex), '_');
      if (includes(knownSlideStainTypes, candidateStainType)) {
        matchedStainType = candidateStainType;
        partIndex = endIndex;
        break;
      }
    }

    if (matchedStainType) {
      viewerStainPreferences.push(matchedStainType);
    } else {
      viewerStainPreferences.push(viewerStainPreferenceParts[partIndex]);
      partIndex += 1;
    }
  }

  return viewerStainPreferences;
};

export const getViewersStainPreferencesForSelectedSlideIds = ({
  selectedSlideIds,
  slides,
  currentViewersStainPreferences,
}: {
  selectedSlideIds: string[];
  slides?: Slide[];
  currentViewersStainPreferences?: Array<string | null> | null;
}) => {
  const sideBySideCurrentViewersStainPreferences = getSideBySideViewerStainPreferences(
    currentViewersStainPreferences,
    slides
  );

  return compact(
    map(
      getSideBySideSelectedSlideIds(selectedSlideIds),
      (slideId, viewerIndex) =>
        find(slides, { id: slideId })?.stainingType ||
        sideBySideCurrentViewersStainPreferences?.[viewerIndex] ||
        sideBySideCurrentViewersStainPreferences?.[0]
    )
  );
};

/**
 * Returns viewer stain preferences for a slide navigation that may cross case boundaries.
 *
 * For same-case navigation the preferences are recomputed from the new slide layout so
 * per-slide stain types are always accurate.
 *
 * For cross-case navigation the current side-by-side preferences are preserved.  The new
 * case has different slides so the IDs in `selectedSlideIdsForNavigation` cannot be used
 * to look up stain types, and mapping over a single target slide would silently truncate
 * a multi-viewer preference list.  The destination case's slide-resolution hook
 * (`useGetDefaultSelectedSlideIds`) will use the preserved preferences to pick the right
 * slides once it has access to the new case's slide list.
 */
export const getViewersStainPreferencesForCaseAwareNavigation = ({
  selectedSlideIdsForNavigation,
  targetCaseId,
  currentCaseId,
  slides,
  currentViewersStainPreferences,
}: {
  selectedSlideIdsForNavigation: string[];
  targetCaseId?: number | null;
  currentCaseId?: number | null;
  slides?: Slide[];
  currentViewersStainPreferences?: Array<string | null> | null;
}): string[] => {
  const isCrossCase =
    typeof currentCaseId === 'number' && typeof targetCaseId === 'number' && currentCaseId !== targetCaseId;

  if (isCrossCase) {
    return getSideBySideViewerStainPreferences(currentViewersStainPreferences, slides);
  }

  return getViewersStainPreferencesForSelectedSlideIds({
    selectedSlideIds: selectedSlideIdsForNavigation,
    slides,
    currentViewersStainPreferences,
  });
};

export const useGetDefaultSelectedSlideIds = (studyId?: string) => {
  const params = useParams();

  const { hasPermission } = usePermissions();
  const isAccessionViewer = hasPermission(Permission.ViewAccessionDashboard);

  const hasTaggingPermissions = hasPermission(Permission.EditSlideTagsAssignments);
  const [slideTagsDrawerOpen] = useQueryParam('slideTagsDrawerOpen', BooleanParam);
  const [slideIdForReviewing] = useQueryParam('slideIdForReviewing', StringParam);

  const { defaultStainingType: defaultStainingTypeFromStudy, defaultSlideViewMode } = useStudyPlatformSettings(studyId);
  const displayOneSlideByDefault = defaultSlideViewMode === 'single';

  const { queryParams } = useEncodedFilters();
  const filteredStainTypes = queryParams?.filters?.stains;
  const [viewersStainPreferences] = useQueryParam('viewersStainPreferences', DelimitedArrayParam);

  const [selectedSlideIds] = useQueryParam('selectedSlideIds', DelimitedArrayParam);

  return useCallback(
    (
      slidesInput: Slide[] = [],
      {
        preferredPrimarySlideId,
      }: {
        preferredPrimarySlideId?: string;
      } = {}
    ): {
      slideIds: string[];
      // The source of the selected slides returned by this function (for debugging)
      source: 'urlPath' | 'filters' | 'queryParams' | 'viewersStainPreferences' | 'study' | 'procedure' | 'tagging';
    } => {
      const slides = compact(slidesInput) as Slide[];
      let slideIdFromUrlPath: string | undefined = preferredPrimarySlideId || params.slideId;
      if (slideIdFromUrlPath && !includes(map(slides, 'id'), slideIdFromUrlPath)) {
        if (!isEmpty(slides)) {
          // If the slide from the URL is not in the procedure, we will ignore it
          console.warn('Slide from URL not found in procedure slides', { slides, slideIdFromUrlPath });
        }
        slideIdFromUrlPath = undefined;
      }
      const slideIdForReviewInCase = findSlideById(slides, slideIdForReviewing)?.id;
      const isTaggingSlide = hasTaggingPermissions && slideTagsDrawerOpen && Boolean(slideIdForReviewInCase);
      const sideBySideViewerStainPreferences = getSideBySideViewerStainPreferences(viewersStainPreferences, slides);
      const selectedSlideIdsInProcedure = getSideBySideSelectedSlideIds(
        map(selectedSlideIds, (selectedSlideId) => findSlideById(slides, selectedSlideId)?.id)
      );

      if (
        !slideIdFromUrlPath &&
        !isTaggingSlide &&
        isEmpty(sideBySideViewerStainPreferences) &&
        !isEmpty(selectedSlideIdsInProcedure)
      ) {
        return { slideIds: selectedSlideIdsInProcedure, source: 'queryParams' };
      }

      // First get good defaults based on tagging and study settings
      const slidesWithFilteredStainTypes = uniq([
        ...filter(slides, (slide) => isPreciseStainMatch(slide, filteredStainTypes)),
        ...filter(slides, (slide) => includes(filteredStainTypes, slide.stainingType)),
      ]);

      const studyDefaultStainType = defaultStainingTypeFromStudy || undefined;
      const canUseStudyDefaultStain =
        Boolean(studyDefaultStainType) && (!filteredStainTypes || includes(filteredStainTypes, studyDefaultStainType));

      const firstSlideIdWithDefaultStainTypeFromStudy: string | undefined =
        canUseStudyDefaultStain && studyDefaultStainType
          ? slides.find((slide) => isPreciseStainMatch(slide, [studyDefaultStainType]))?.id ||
            slides.find((slide) => slide.stainingType === studyDefaultStainType)?.id
          : undefined;

      const slidesThatAreNotControls = filter(slides, (slide) => !slide.positiveControl && !slide.negativeControl);

      const firstSlideIdMatchingFilteredStainTypes = first(slidesWithFilteredStainTypes)?.id;

      const defaultFirstSlideId = slideIdFromUrlPath
        ? slideIdFromUrlPath
        : isTaggingSlide
        ? // Use the slide for review as the first slide if the user is tagging a slide
          slideIdForReviewInCase
        : // otherwise use the first slide with the default stain type
          firstSlideIdWithDefaultStainTypeFromStudy ||
          // otherwise use the first slide with a stain type from the filters
          firstSlideIdMatchingFilteredStainTypes ||
          // otherwise use the first slide from the procedure that isn't a control
          first(slidesThatAreNotControls)?.id ||
          // otherwise use the first slide from the procedure
          first(slides)?.id;

      const defaultSecondSlideId =
        // Use the first slide with a stain type from the filters that is not the first slide
        find(slidesWithFilteredStainTypes, (slide) => slide.id !== defaultFirstSlideId)?.id ||
        // otherwise use the first slide from the procedure that is not the first slide
        find(slides, (slide) => slide.id !== defaultFirstSlideId)?.id;

      const defaultsWithoutViewerStainPreferences = compact([defaultFirstSlideId, defaultSecondSlideId]);

      if (!isEmpty(sideBySideViewerStainPreferences)) {
        // If the user has set their preferences, we will try to match them
        const defaultSlidesWithoutViewerStainPreferences = compact(
          map(defaultsWithoutViewerStainPreferences, (id) => findSlideById(slides, id))
        ) as Slide[];
        const matchingSlides: string[] = [];
        forEach(sideBySideViewerStainPreferences, (stainType, viewerIndex) => {
          if (!stainType) {
            return;
          }
          if (isTaggingSlide && viewerIndex === 0) {
            // If the user is tagging a slide, we want to make sure the slide for review is the first one
            if (slideIdForReviewInCase) {
              matchingSlides.push(slideIdForReviewInCase);
            }
            return;
          }
          // If a slide has already been selected and is in the procedure, we will keep it
          const currentlySelectedSlide = selectedSlideIds?.[viewerIndex];
          if (currentlySelectedSlide && includes(map(slides, 'id'), currentlySelectedSlide)) {
            matchingSlides.push(currentlySelectedSlide);
            return;
          }
          const findNewPreciseStainMatch = (slide: Slide) =>
            isPreciseStainMatch(slide, [stainType]) && !includes(matchingSlides, slide?.id);

          const findNewStainMatch = (slide: Slide) =>
            slide?.stainingType === stainType && !includes(matchingSlides, slide?.id);

          const matchingSlideFromDefaults =
            defaultSlidesWithoutViewerStainPreferences.find(findNewPreciseStainMatch)?.id ||
            defaultSlidesWithoutViewerStainPreferences.find(findNewStainMatch)?.id;
          if (matchingSlideFromDefaults) {
            matchingSlides.push(matchingSlideFromDefaults);
            return;
          }
          const matchingSlideFromAllSlides =
            slides.find(findNewPreciseStainMatch)?.id || slides.find(findNewStainMatch)?.id;

          if (matchingSlideFromAllSlides) {
            matchingSlides.push(matchingSlideFromAllSlides);
          }
        });
        if (slideIdFromUrlPath && !includes(matchingSlides, slideIdFromUrlPath)) {
          // If the user has a slide in the URL, we will make sure it is in the selected slides, regardless of the viewer preferences
          if (size(matchingSlides) > 1) {
            // If there are already two slides selected, we will replace the second one, to avoid messing with tagging
            matchingSlides[1] = slideIdFromUrlPath;
          } else {
            matchingSlides[0] = slideIdFromUrlPath;
          }
        }
        if (!isEmpty(matchingSlides)) {
          const requestedViewerCount = Math.min(
            MAX_SIDE_BY_SIDE_VIEWERS,
            Math.max(size(sideBySideViewerStainPreferences), size(getSideBySideSelectedSlideIds(selectedSlideIds)))
          );
          const primaryMatchingSlideId = first(matchingSlides);

          while (primaryMatchingSlideId && size(matchingSlides) < requestedViewerCount) {
            matchingSlides.push(primaryMatchingSlideId);
          }

          return { slideIds: getSideBySideSelectedSlideIds(matchingSlides), source: 'viewersStainPreferences' };
        }
      }
      // Otherwise, we will show the two default slides, unless the user is tagging a slide, there is only one slide, or the user has set the viewer to show one slide by default
      const slideIds = compact(
        !isTaggingSlide && size(slides) > 1 && !isAccessionViewer && !displayOneSlideByDefault
          ? defaultsWithoutViewerStainPreferences
          : [first(defaultsWithoutViewerStainPreferences)]
      );

      const newFirstSlideId = first(slideIds);
      const doSlidesMatchStudySettings =
        firstSlideIdWithDefaultStainTypeFromStudy === newFirstSlideId &&
        size(slideIds) === (displayOneSlideByDefault ? 1 : 2);
      const source = doSlidesMatchStudySettings
        ? 'study'
        : slideIdFromUrlPath === newFirstSlideId
        ? 'urlPath'
        : isTaggingSlide
        ? 'tagging'
        : firstSlideIdMatchingFilteredStainTypes === newFirstSlideId
        ? 'filters'
        : 'procedure';
      return { slideIds, source };
    },
    [
      hasTaggingPermissions,
      slideTagsDrawerOpen,
      slideIdForReviewing,
      defaultStainingTypeFromStudy,
      filteredStainTypes,
      viewersStainPreferences,
      selectedSlideIds,
      isAccessionViewer,
      displayOneSlideByDefault,
    ]
  );
};

/**
 * hook for managing the selectedSlideIds query param
 * @param procedure the procedure to get the default selected slides from (if needed)
 * @returns the current selectedSlideIds from the query param
 */
export const useSelectedSlideIds = (procedure?: Procedure) => {
  const getDefaultSelectedSlideIds = useGetDefaultSelectedSlideIds(procedure?.studyId);

  const { slideIds: defaultSelectedSlideIds } = useMemo(
    () =>
      procedure
        ? getDefaultSelectedSlideIds(procedure?.slides)
        : ({ slideIds: [], source: 'procedure' } as ReturnType<typeof getDefaultSelectedSlideIds>),
    [procedure?.slides]
  );

  // We try to start the viewer with good defaults, but will change the selectedSlideIds as needed to reflect study settings and user actions
  const SelectedSlideIdsParam = useMemo(
    () => withDefault(DelimitedArrayParam, defaultSelectedSlideIds),
    [defaultSelectedSlideIds]
  );
  const [selectedSlideIds, setSelectedSlideIds] = useQueryParam('selectedSlideIds', SelectedSlideIdsParam);
  const nextSideBySideSelectedSlideIds = getSideBySideSelectedSlideIds(selectedSlideIds);
  const sideBySideSelectedSlideIdsRef = useRef(nextSideBySideSelectedSlideIds);
  if (!isEqual(sideBySideSelectedSlideIdsRef.current, nextSideBySideSelectedSlideIds)) {
    sideBySideSelectedSlideIdsRef.current = nextSideBySideSelectedSlideIds;
  }
  const sideBySideSelectedSlideIds = sideBySideSelectedSlideIdsRef.current;

  const selectedSlideIdsInProcedure = useMemo(
    () =>
      getSideBySideSelectedSlideIds(
        map(sideBySideSelectedSlideIds, (selectedSlideId) => findSlideById(procedure?.slides, selectedSlideId)?.id)
      ),
    [procedure?.slides, sideBySideSelectedSlideIds]
  );

  const sanitizedSelectedSlideIds = useMemo(() => {
    if (
      !procedure ||
      isEmpty(sideBySideSelectedSlideIds) ||
      isEqual(selectedSlideIdsInProcedure, sideBySideSelectedSlideIds)
    ) {
      return sideBySideSelectedSlideIds;
    }

    return getSideBySideSelectedSlideIds(defaultSelectedSlideIds);
  }, [defaultSelectedSlideIds, procedure, sideBySideSelectedSlideIds, selectedSlideIdsInProcedure]);

  return [sanitizedSelectedSlideIds, setSelectedSlideIds] as ReturnType<typeof useQueryParam<string[]>>;
};

export const useSetSelectedSlideIds = (procedure: Procedure) => {
  const [selectedSlideIds, setSelectedSlideIdsParam] = useSelectedSlideIds(procedure);
  const [viewersStainPreferences, setViewersStainPreferences] = useQueryParam(
    'viewersStainPreferences',
    DelimitedArrayParam
  );

  const setSelectedSlideIds = useCallback(
    (newSetSelectedSlideIds: string[]) => {
      // Make sure the selected slides are in the procedure
      const nextSideBySideSelectedSlideIds = getSideBySideSelectedSlideIds(newSetSelectedSlideIds);
      const slidesFromProcedure = compact(map(nextSideBySideSelectedSlideIds, (id) => find(procedure?.slides, { id })));
      const newSelectedSlides = map(slidesFromProcedure, 'id');
      if (!isEqual(newSelectedSlides, selectedSlideIds)) {
        setSelectedSlideIdsParam(newSelectedSlides, 'replaceIn');
      }
      // Update the viewersStainPreferences based on the selected slides
      const newStainPreferences = getViewersStainPreferencesForSelectedSlideIds({
        selectedSlideIds: newSelectedSlides,
        slides: procedure?.slides,
        currentViewersStainPreferences: viewersStainPreferences,
      });
      if (!isEqual(newStainPreferences, viewersStainPreferences)) {
        setViewersStainPreferences(newStainPreferences, 'replaceIn');
      }
    },
    [setSelectedSlideIdsParam, procedure?.slides, selectedSlideIds, viewersStainPreferences, setViewersStainPreferences]
  );

  const setFirstSlideId = useCallback(
    (id: string) => {
      setSelectedSlideIds([id, ...slice(selectedSlideIds, 1, MAX_SIDE_BY_SIDE_VIEWERS)]);
    },
    [selectedSlideIds, setSelectedSlideIds]
  );

  return {
    selectedSlideIds,
    viewersStainPreferences,
    setSelectedSlideIds,
    setFirstSlideId,
  };
};

export const useSyncSelectedSlideIds = (procedure: Procedure, debug: boolean = false) => {
  const studyId = procedure?.studyId;
  const getDefaultSelectedSlideIds = useGetDefaultSelectedSlideIds(studyId);

  const { selectedSlideIds, setSelectedSlideIds, viewersStainPreferences } = useSetSelectedSlideIds(procedure);
  const { hasPermission } = usePermissions();

  const hasTaggingPermissions = hasPermission(Permission.EditSlideTagsAssignments);
  const [slideTagsDrawerOpen] = useQueryParam('slideTagsDrawerOpen', BooleanParam);
  const [slideIdForReviewing, setSlideIdForReviewing] = useQueryParam('slideIdForReviewing', StringParam);
  const slideIdForReviewInCase = findSlideById(procedure?.slides, slideIdForReviewing)?.id;
  const isTaggingSlide = hasTaggingPermissions && slideTagsDrawerOpen && Boolean(slideIdForReviewInCase);

  const { isLoadingStudySettings } = useStudyPlatformSettings(studyId);
  const [studySettingsApplied, setStudySettingsApplied] = useQueryParam('studySettingsApplied', StringParam);

  // Sync the selectedSlideIds query param when starting to tag a slide
  useEffect(() => {
    if (debug) {
      console.debug('useSyncSelectedSlideIds - update based on tagging state', procedure.id, {
        selectedSlideIds,
        viewersStainPreferences,
        studySettingsApplied,
        isLoadingStudySettings,
      });
    }
    if (slideIdForReviewing && !slideIdForReviewInCase) {
      console.warn('Slide for reviewing not found in procedure slides', {
        slideIdForReviewing,
        slides: map(procedure?.slides, 'id'),
      });
      setSlideIdForReviewing(undefined, 'replaceIn');
    } else if (isTaggingSlide && slideIdForReviewInCase) {
      // If the user asked to tag a slide, make sure it is selected on the first viewer
      if (first(selectedSlideIds) === slideIdForReviewInCase) {
        return;
      } else if (includes(selectedSlideIds, slideIdForReviewInCase)) {
        console.info('Moving slide for review to the first viewer');
        setSelectedSlideIds([
          slideIdForReviewInCase,
          ...filter(selectedSlideIds, (id) => id !== slideIdForReviewInCase),
        ]);
      } else {
        console.info('Adding slide for review to the first viewer');
        setSelectedSlideIds([slideIdForReviewInCase, ...slice(selectedSlideIds, 1)]);
      }
    }
  }, [isTaggingSlide, slideIdForReviewInCase, selectedSlideIds]);

  useEffect(() => {
    if (debug) {
      console.debug('useSyncSelectedSlideIds - update based on study settings', procedure.id, {
        selectedSlideIds,
        viewersStainPreferences,
        studySettingsApplied,
        isLoadingStudySettings,
      });
    }
    const { source: defaultSelectedSlideSource, slideIds: newSelectedSlideIds } = getDefaultSelectedSlideIds(
      procedure?.slides
    );
    const selectedSlideIdsInProcedure = compact(
      map(selectedSlideIds, (selectedSlideId) => find(procedure?.slides, { id: selectedSlideId })?.id)
    );
    const shouldReconcileSelectedSlideIds =
      !isEmpty(viewersStainPreferences) &&
      !isEqual(newSelectedSlideIds, selectedSlideIds) &&
      (!isEqual(selectedSlideIdsInProcedure, selectedSlideIds) || size(selectedSlideIds) !== size(newSelectedSlideIds));

    if (shouldReconcileSelectedSlideIds) {
      if (debug) {
        console.debug('Reconciling selected slides based on viewer stain preferences');
      }
      setSelectedSlideIds(newSelectedSlideIds);
      return;
    }

    if (studyId && !isLoadingStudySettings && studySettingsApplied !== studyId && isEmpty(viewersStainPreferences)) {
      // Update selected slides, if needed, based on the study settings
      if (
        !isEqual(newSelectedSlideIds, selectedSlideIds) &&
        // Only make the change if the slide for review is the first one if it is selected
        // Otherwise we will have a race between the useEffects
        (!slideIdForReviewInCase || first(newSelectedSlideIds) === slideIdForReviewInCase)
      ) {
        console.info('Updating selected slides based on study settings');
        if (defaultSelectedSlideSource === 'study') {
          setStudySettingsApplied(studyId, 'replaceIn');
        } else {
          console.warn('New selected slides do not match study settings', {
            selectedSlideIds,
            newSelectedSlideIds,
            defaultSelectedSlideSource,
          });
        }
        setSelectedSlideIds(newSelectedSlideIds);
      }
    } else if (isEmpty(selectedSlideIds)) {
      // If no slides are selected, set the default selected slides
      console.info('Setting default selected slides');
      setSelectedSlideIds(newSelectedSlideIds);
    }
  }, [
    isLoadingStudySettings,
    studySettingsApplied,
    viewersStainPreferences,
    selectedSlideIds,
    procedure?.slides,
    slideIdForReviewInCase,
    setSelectedSlideIds,
    getDefaultSelectedSlideIds,
  ]);
};
